// navCustomization.ts — Alapalkin mukauttaminen (20.8.2026, laajennettu 27.8.2026).
//
// Käyttäjä: "noi alhaalla olevat projekti yms pitäisi pystyä itse määrittämään
// mitä niistä tulee. Eli kun painaa pitkään niin tulee se lisää valikon
// vaihtoehdot ja klikkaamalla saa sen tuohon alas".
//
// Alapalkki oli kiinteä: operaattorille aina Tänään/Työ/AI/Lisää, johdolle
// lisäksi Tilanne ja Projektit. Eri ihmiset käyttävät eri sivuja päivittäin —
// pinnoittaja Liukulinja-ajoja, laadusta vastaava Korroosiovahtia — ja
// molemmat joutuivat kiertämään Lisää-valikon kautta joka kerta.
//
// 27.8.2026 (käyttäjän pyyntö): Tänään ja Työ ovat nyt kiinteitä Lisää-paikan
// tapaan — päivän ydin (kellokortti + työtehtävät) löytyy aina samasta
// kohdasta. Lisäksi paikkojen MÄÄRÄN voi valita Asetuksista (3–6): yli kuusi
// menee puhelimella liian ahtaaksi. Järjestys: Tänään 1., Työ 2., Lisää aina
// viimeisenä; vapaat paikat niiden väliin.
//
// Valinnat tallentuvat SELAIMEEN (localStorage) eikä kantaan: ne ovat
// laitekohtaisia tottumuksia, eivät jaettavia asetuksia, eikä niiden takia
// kannata tehdä migraatiota. Jos sama ihminen käyttää kahta laitetta,
// kummallakin saa olla oma järjestys.

/** Kiinteät paikat (27.8.2026): näitä ei voi vaihtaa. Ilman Lisää-valikkoa
 *  muille sivuille ei pääse; Tänään ja Työ ovat päivittäinen ydin. */
export const FIXED_TAB_IDS = ['today', 'work', 'more'] as const;

export function isFixedTab(tabId: string): boolean {
  return (FIXED_TAB_IDS as readonly string[]).includes(tabId);
}

const KEY = 'savroc.nav.custom.v1';
const COUNT_KEY = 'savroc.nav.count.v1';

/** { paikan alkuperäinen id → valittu polku }. Tyhjä = oletusvalikko. */
export type NavOverrides = Record<string, string>;

export function readOverrides(raw: string | null): NavOverrides {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: NavOverrides = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      // Vain merkkijonopolut kelpaavat — rikkinäinen tallenne ei saa kaataa navia
      if (typeof v === 'string' && v.startsWith('/')) out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

export function loadOverrides(): NavOverrides {
  if (typeof window === 'undefined') return {};
  return readOverrides(window.localStorage.getItem(KEY));
}

export function saveOverrides(next: NavOverrides): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Yksityinen selaustila tms. — mukautus jää voimaan vain istunnon ajaksi
  }
}

// ── Paikkojen määrä (27.8.2026) ─────────────────────────────────────────────

/** Vähintään pelkät kiinteät paikat. */
export const MIN_TAB_COUNT = FIXED_TAB_IDS.length;
/** Yli 6 menee puhelimella liian ahtaaksi — kova katto kaikille rooleille. */
export const MAX_TAB_COUNT = 6;

/** Oletus = entinen kiinteä määrä: operaattorilla 4, johdolla 6. */
export function defaultTabCount(isExec: boolean): number {
  return isExec ? MAX_TAB_COUNT : 4;
}

export function clampTabCount(n: number): number {
  return Math.min(MAX_TAB_COUNT, Math.max(MIN_TAB_COUNT, Math.round(n)));
}

export function readTabCount(raw: string | null, fallback: number): number {
  if (!raw) return fallback;
  const n = Number(raw);
  // Rikkinäinen tallenne ei saa kaataa navia — takaisin roolin oletukseen
  if (!Number.isInteger(n)) return fallback;
  return clampTabCount(n);
}

export function loadTabCount(fallback: number): number {
  if (typeof window === 'undefined') return fallback;
  return readTabCount(window.localStorage.getItem(COUNT_KEY), fallback);
}

/** Tapahtuma jolla kuori huomaa Asetuksissa tehdyn määrämuutoksen heti —
 *  alapalkki päivittyy samalla sekunnilla kun valinta tehdään. */
export const TAB_COUNT_EVENT = 'savroc:nav-count';

export function saveTabCount(n: number): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(COUNT_KEY, String(clampTabCount(n)));
  } catch {
    // Yksityinen selaustila tms. — valinta jää voimaan vain istunnon ajaksi
  }
  window.dispatchEvent(new Event(TAB_COUNT_EVENT));
}

// ── Paikkojen koostaminen (27.8.2026) ───────────────────────────────────────

export interface ShellTab {
  id: string;
  path: string;
  icon: string;
  label: string;
  /** Tyhjä vapaa paikka: näyttää kevyen "+"-tilan josta sivuvalitsin aukeaa. */
  empty?: boolean;
}

const TODAY_TAB: ShellTab = { id: 'today', path: '/op', icon: '☀️', label: 'Tänään' };
const WORK_TAB: ShellTab = { id: 'work', path: '/op/work', icon: '🔧', label: 'Työ' };
const AI_TAB: ShellTab = { id: 'ai', path: '/op/ai', icon: '🤖', label: 'AI' };

// Vapaat paikat järjestyksessä — täytetään kun määrää kasvatetaan.
// Operaattorilla AI on ensimmäinen vapaa, loput tyhjiä "+"-paikkoja.
// Johdolla entiset EXEC-extrat: Tilanne, Projektit, AI.
const OPERATOR_FREE_SLOTS: ShellTab[] = [
  AI_TAB,
  { id: 'vapaa2', path: '', icon: '＋', label: 'Valitse', empty: true },
  { id: 'vapaa3', path: '', icon: '＋', label: 'Valitse', empty: true },
];
const EXEC_FREE_SLOTS: ShellTab[] = [
  { id: 'tilannekuva', path: '/exec/tilannekuva', icon: '📊', label: 'Tilanne' },
  { id: 'projects', path: '/exec/projects', icon: '📁', label: 'Projektit' },
  AI_TAB,
];

/**
 * Alapalkin paikat: Tänään + Työ, sitten niin monta vapaata paikkaa kuin
 * määrä sallii, ja Lisää AINA viimeisenä. Kun määrää pienennetään, ylimmät
 * vapaat paikat piiloutuvat — niiden mukautukset säilyvät tallessa ja
 * palaavat jos määrä nostetaan takaisin.
 */
export function baseTabsForRole(isExec: boolean, count: number): ShellTab[] {
  const free = (isExec ? EXEC_FREE_SLOTS : OPERATOR_FREE_SLOTS)
    .slice(0, clampTabCount(count) - FIXED_TAB_IDS.length);
  return [
    TODAY_TAB,
    WORK_TAB,
    ...free,
    { id: 'more', path: isExec ? '/exec/more' : '/op/more', icon: '⋯', label: 'Lisää' },
  ];
}

export interface NavChoice {
  icon: string;
  label: string;
  path: string;
}

/** Valitsimeen myös roolin oletussivut (AI; johdolla Tilanne/Projektit) —
 *  ne eivät ole Lisää-valikossa, mutta ne pitää saada takaisin vaikka paikka
 *  olisi vaihdettu toiseksi tai piilotettu määrää pienentämällä. */
export function freeSlotChoices(isExec: boolean): NavChoice[] {
  return (isExec ? EXEC_FREE_SLOTS : OPERATOR_FREE_SLOTS)
    .filter(t => !t.empty)
    .map(t => ({ icon: t.icon, label: t.label, path: t.path }));
}

/**
 * Sovittaa tallennetut mukautukset paikkoihin. Kiinteiden paikkojen
 * (Tänään/Työ/Lisää) vanhat overridet ohitetaan siististi — sama fallback
 * kuin tuntemattomalle polulle: paikka näyttää oletuksensa.
 */
export function resolveTabs(
  baseTabs: ShellTab[],
  overrides: NavOverrides,
  choices: NavChoice[],
): ShellTab[] {
  return baseTabs.map(t => {
    if (isFixedTab(t.id)) return t;
    const path = overrides[t.id];
    if (!path) return t;
    const item = choices.find(c => c.path === path)
      ?? baseTabs.find(b => b.id !== t.id && b.path === path);
    if (item) return { ...t, path, icon: item.icon, label: item.label, empty: false };
    // Tallennettu polku ei vastaa mitään tunnettua sivua → oletus takaisin
    return t;
  });
}

/**
 * Korvaa paikan valitulla sivulla. Kiinteää paikkaa ei voi vaihtaa, eikä samaa
 * sivua voi olla kahdessa paikassa — jos valittu sivu on jo jossain, paikat
 * vaihtavat keskenään sen sijaan että sama nappi olisi kahdesti.
 */
export function applyChoice(
  overrides: NavOverrides,
  tabs: { id: string; path: string }[],
  targetTabId: string,
  chosenPath: string,
): NavOverrides {
  if (isFixedTab(targetTabId)) return overrides;

  const effective = (t: { id: string; path: string }) => overrides[t.id] ?? t.path;

  // Onko valittu sivu jo jossain toisessa paikassa? Silloin vaihdetaan.
  // Kiinteän paikan sivua (esim. /op/work) ei voi tuoda toiseen paikkaan —
  // kiinteä ei vaihdu pois, joten sama nappi olisi kahdesti.
  const occupied = tabs.find(t => t.id !== targetTabId && effective(t) === chosenPath);
  if (occupied && isFixedTab(occupied.id)) return overrides;

  const next: NavOverrides = { ...overrides };
  if (occupied) {
    const targetPath = effective(tabs.find(t => t.id === targetTabId)!);
    // Tyhjän "+"-paikan polku on '' — silloin toinen paikka ei saa "tyhjää"
    // vaan palautuu omaan oletukseensa (27.8.2026).
    if (targetPath) next[occupied.id] = targetPath;
    else delete next[occupied.id];
  }
  next[targetTabId] = chosenPath;
  return next;
}

/** Palauttaa oletusvalikon yhdelle paikalle. */
export function resetTab(overrides: NavOverrides, tabId: string): NavOverrides {
  const next = { ...overrides };
  delete next[tabId];
  return next;
}
